import Vue from 'vue'
import VueI18n from 'vue-i18n'

// 导入语言包
import zhCN from './locales/zh-CN'
import enUS from './locales/en-US'

// 导入工具函数
import { getLanguageFromURL, updateHTMLLang, warmupLanguageCache } from './utils'

// 导入错误处理器
import { 
  I18nErrorHandler, 
  I18N_ERROR_TYPES,
  i18nErrorHandler 
} from '../utils/I18nErrorHandler'

// 导入资源缓存管理器
import resourceCache from './ResourceCache'

// 导入移动端优化器
import mobileOptimizer from './MobileOptimizer'

// 使用 VueI18n 插件
Vue.use(VueI18n)

// 安全的语言包加载函数（优化版本）
function safeLoadMessages() {
  try {
    const messages = {
      'zh-CN': zhCN,
      'en-US': enUS
    }
    
    // 异步预加载常用翻译到缓存（移动端优化）
    setTimeout(() => {
      const currentLanguage = getLanguageFromURL()
      if (messages[currentLanguage]) {
        // 根据设备性能调整缓存配置
        const deviceConfig = mobileOptimizer.currentConfig
        resourceCache.config.maxCacheSize = deviceConfig.maxCacheSize
        resourceCache.config.maxMemoryUsage = deviceConfig.memoryLimit
        
        resourceCache.preloadTranslations(currentLanguage, messages[currentLanguage])
          .then(() => {
            console.log('翻译资源预加载完成 (移动端优化)')
          })
          .catch(error => {
            console.warn('翻译资源预加载失败:', error)
          })
      }
    }, 0)
    
    return messages
  } catch (error) {
    // 语言包加载失败时的降级处理
    I18nErrorHandler.handleError(
      I18N_ERROR_TYPES.RESOURCE_LOAD_FAILED,
      error,
      { context: 'loading language packs' }
    )
    
    // 返回最小化的语言包
    return {
      'zh-CN': {
        common: {
          loading: '加载中...',
          error: '出错了',
          retry: '重试'
        }
      },
      'en-US': {
        common: {
          loading: 'Loading...',
          error: 'Error occurred',
          retry: 'Retry'
        }
      }
    }
  }
}

// 创建 i18n 实例
let i18n
try {
  i18n = new VueI18n({
    locale: 'zh-CN', // 默认语言为中文
    fallbackLocale: 'zh-CN', // 回退语言为中文
    messages: safeLoadMessages(),
    // 禁用警告信息（可选）
    silentTranslationWarn: true,
    // 禁用回退警告（可选）
    silentFallbackWarn: true,
    // 自定义缺失翻译处理器
    missing: (locale, key, vm, values) => {
      return I18nErrorHandler.handleMissingTranslation(key, locale, values)
    },
    // 自定义翻译后处理器
    postTranslation: (str, key) => {
      // 如果翻译结果为空或undefined，使用错误处理
      if (!str || str === key) {
        return I18nErrorHandler.handleMissingTranslation(key, i18n.locale)
      }
      return str
    }
  })
} catch (error) {
  // i18n 实例创建失败时的降级处理
  I18nErrorHandler.handleError(
    I18N_ERROR_TYPES.INITIALIZATION_FAILED,
    error,
    { context: 'creating VueI18n instance' }
  )
  
  // 创建最小化的 i18n 实例
  i18n = {
    locale: 'zh-CN',
    fallbackLocale: 'zh-CN',
    t: (key) => {
      return I18nErrorHandler.handleTranslationError(key, new Error('i18n instance not available'), {
        originalText: key.split('.').pop() || key
      })
    },
    tc: (key) => {
      return I18nErrorHandler.handleTranslationError(key, new Error('i18n instance not available'))
    }
  }
}

// 初始化语言设置
function initLanguage() {
  try {
    // 预热语言检测缓存，提升性能
    warmupLanguageCache()
    
    // 从 URL 检测语言参数
    const detectedLanguage = getLanguageFromURL()
    
    console.log('检测到的语言:', detectedLanguage)
    
    // 验证检测到的语言是否支持
    if (!['zh-CN', 'en-US'].includes(detectedLanguage)) {
      return I18nErrorHandler.handleError(
        I18N_ERROR_TYPES.LANGUAGE_NOT_SUPPORTED,
        `Unsupported language: ${detectedLanguage}`,
        {
          detectedLanguage,
          i18nInstance: i18n,
          defaultValue: 'zh-CN'
        }
      )
    }
    
    // 设置 i18n 实例的语言
    if (i18n && typeof i18n.locale !== 'undefined') {
      i18n.locale = detectedLanguage
    } else {
      throw new Error('i18n instance is not properly initialized')
    }
    
    // 更新 HTML lang 属性
    updateHTMLLang(detectedLanguage)
    
    // 触发语言变化事件（如果需要）
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('languageChanged', {
        detail: { locale: detectedLanguage }
      }))
    }
    
    // 重置相关错误计数（初始化成功）
    i18nErrorHandler.resetErrorCount(I18N_ERROR_TYPES.INITIALIZATION_FAILED)
    i18nErrorHandler.resetErrorCount(I18N_ERROR_TYPES.LANGUAGE_NOT_SUPPORTED)
    
    return detectedLanguage
  } catch (error) {
    // 使用错误处理器处理初始化失败
    return I18nErrorHandler.handleError(
      I18N_ERROR_TYPES.INITIALIZATION_FAILED,
      error,
      {
        i18nInstance: i18n,
        defaultValue: 'zh-CN',
        retryFunction: () => {
          // 重试初始化逻辑
          const fallbackLanguage = 'zh-CN'
          if (i18n && typeof i18n.locale !== 'undefined') {
            i18n.locale = fallbackLanguage
          }
          updateHTMLLang(fallbackLanguage)
          return fallbackLanguage
        }
      }
    )
  }
}

// 设置语言的函数
function setLanguage(locale) {
  try {
    // 验证语言代码
    if (!locale || !['zh-CN', 'en-US'].includes(locale)) {
      return I18nErrorHandler.handleError(
        I18N_ERROR_TYPES.LANGUAGE_NOT_SUPPORTED,
        `Unsupported language code: ${locale}`,
        {
          locale,
          i18nInstance: i18n,
          defaultValue: i18n.locale || 'zh-CN'
        }
      )
    }
    
    // 检查是否需要切换语言
    if (i18n && i18n.locale !== locale) {
      console.log('切换语言:', i18n.locale, '->', locale)
      
      // 更新 i18n 实例的语言
      if (typeof i18n.locale !== 'undefined') {
        i18n.locale = locale
      } else {
        throw new Error('i18n instance locale property is not accessible')
      }
      
      // 更新 HTML lang 属性
      updateHTMLLang(locale)
      
      // 清除旧语言的缓存，预加载新语言
      const oldLocale = i18n.locale
      if (oldLocale !== locale) {
        resourceCache.clearCache(oldLocale)
        
        // 异步预加载新语言的常用翻译
        setTimeout(() => {
          const messages = i18n.messages && i18n.messages[locale]
          if (messages) {
            resourceCache.preloadTranslations(locale, messages)
              .catch(error => console.warn('新语言预加载失败:', error))
          }
        }, 0)
      }
      
      // 触发语言变化事件
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('languageChanged', {
          detail: { locale: locale, oldLocale: oldLocale }
        }))
      }
      
      // 重置语言切换错误计数
      i18nErrorHandler.resetErrorCount(I18N_ERROR_TYPES.LANGUAGE_SWITCH_FAILED)
    }
    
    return locale
  } catch (error) {
    // 使用错误处理器处理语言切换失败
    return I18nErrorHandler.handleError(
      I18N_ERROR_TYPES.LANGUAGE_SWITCH_FAILED,
      error,
      {
        targetLocale: locale,
        currentLocale: i18n ? i18n.locale : 'unknown',
        i18nInstance: i18n,
        retryFunction: () => {
          // 重试语言切换
          if (i18n && typeof i18n.locale !== 'undefined') {
            i18n.locale = locale
            updateHTMLLang(locale)
            return locale
          }
          throw new Error('Retry failed: i18n instance not available')
        }
      }
    )
  }
}

// 获取当前语言
function getCurrentLanguage() {
  try {
    if (i18n && typeof i18n.locale !== 'undefined') {
      return i18n.locale
    } else {
      throw new Error('i18n instance is not available')
    }
  } catch (error) {
    // 获取当前语言失败时的错误处理
    return I18nErrorHandler.handleError(
      I18N_ERROR_TYPES.UNKNOWN_ERROR,
      error,
      {
        context: 'getting current language',
        defaultValue: 'zh-CN'
      }
    )
  }
}

// 自动初始化语言设置
// 在模块加载时立即执行语言检测和初始化
let initialLanguage
try {
  initialLanguage = initLanguage()
  console.log('国际化系统初始化完成，当前语言:', initialLanguage)
} catch (error) {
  // 初始化过程中的任何未捕获错误
  initialLanguage = I18nErrorHandler.handleError(
    I18N_ERROR_TYPES.INITIALIZATION_FAILED,
    error,
    {
      context: 'module initialization',
      defaultValue: 'zh-CN'
    }
  )
  console.error('国际化系统初始化失败，使用降级方案:', initialLanguage)
}

// 监听 URL 变化，动态更新语言设置
if (typeof window !== 'undefined') {
  // 安全的事件处理函数
  const handleLanguageChange = (eventType) => {
    try {
      const newLanguage = getLanguageFromURL()
      const currentLanguage = getCurrentLanguage()
      
      if (newLanguage !== currentLanguage) {
        console.log(`${eventType}检测到语言切换:`, currentLanguage, '->', newLanguage)
        setLanguage(newLanguage)
      }
    } catch (error) {
      I18nErrorHandler.handleError(
        I18N_ERROR_TYPES.URL_PARSE_ERROR,
        error,
        {
          eventType,
          context: 'handling language change event'
        }
      )
    }
  }
  
  // 监听 popstate 事件（浏览器前进后退）
  window.addEventListener('popstate', () => {
    handleLanguageChange('URL变化')
  })
  
  // 监听 hashchange 事件（hash路由变化）
  window.addEventListener('hashchange', () => {
    handleLanguageChange('Hash变化')
  })
  
  // 全局错误处理 - 捕获未处理的国际化相关错误
  window.addEventListener('error', (event) => {
    // 检查是否是国际化相关错误
    if (event.error && (
      event.error.message.includes('i18n') ||
      event.error.message.includes('translation') ||
      event.error.message.includes('locale')
    )) {
      I18nErrorHandler.handleError(
        I18N_ERROR_TYPES.UNKNOWN_ERROR,
        event.error,
        {
          context: 'global error handler',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        }
      )
    }
  })
  
  // 全局 Promise 拒绝处理 - 捕获异步国际化错误
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && typeof event.reason === 'object' && (
      (event.reason.message && event.reason.message.includes('i18n')) ||
      (event.reason.type && event.reason.type.includes('i18n'))
    )) {
      I18nErrorHandler.handleError(
        I18N_ERROR_TYPES.UNKNOWN_ERROR,
        event.reason,
        {
          context: 'unhandled promise rejection',
          promise: event.promise
        }
      )
      
      // 防止错误在控制台显示
      event.preventDefault()
    }
  })
}

// 创建安全的翻译函数包装器（优化版本）
function createSafeTranslationWrapper(i18nInstance) {
  return {
    // 安全的翻译函数（带缓存和移动端优化）
    t: mobileOptimizer.optimizeTranslateFunction((key, values) => {
      try {
        if (!i18nInstance || typeof i18nInstance.t !== 'function') {
          throw new Error('i18n instance or t function not available')
        }
        
        const locale = i18nInstance.locale || 'zh-CN'
        
        // 对于简单的翻译（无参数），尝试从缓存获取
        if (!values || Object.keys(values).length === 0) {
          const messages = i18nInstance.messages && i18nInstance.messages[locale]
          if (messages) {
            const cachedResult = resourceCache.getTranslation(locale, key, messages)
            if (cachedResult !== null) {
              // 增加访问计数
              resourceCache.incrementAccessCount(locale, key)
              return cachedResult
            }
          }
        }
        
        // 使用原始i18n实例进行翻译
        const result = i18nInstance.t(key, values)
        
        // 检查翻译结果是否有效
        if (!result || result === key) {
          return I18nErrorHandler.handleMissingTranslation(key, locale, values)
        }
        
        return result
      } catch (error) {
        return I18nErrorHandler.handleTranslationError(key, error, {
          values,
          originalText: key.split('.').pop() || key
        })
      }
    }),
    
    // 安全的复数翻译函数
    tc: (key, choice, values) => {
      try {
        if (!i18nInstance || typeof i18nInstance.tc !== 'function') {
          throw new Error('i18n instance or tc function not available')
        }
        
        return i18nInstance.tc(key, choice, values)
      } catch (error) {
        return I18nErrorHandler.handleTranslationError(key, error, {
          choice,
          values,
          originalText: key.split('.').pop() || key
        })
      }
    },
    
    // 代理其他属性
    get locale() {
      return getCurrentLanguage()
    },
    
    set locale(value) {
      setLanguage(value)
    },
    
    get fallbackLocale() {
      return i18nInstance ? i18nInstance.fallbackLocale : 'zh-CN'
    },
    
    get messages() {
      return i18nInstance ? i18nInstance.messages : {}
    }
  }
}

// 创建安全的 i18n 包装器
const safeI18n = createSafeTranslationWrapper(i18n)

// 导出 i18n 实例和相关函数
export default safeI18n

// 性能统计函数
function getI18nPerformanceStats() {
  return {
    cache: resourceCache.getStats(),
    mobile: mobileOptimizer.getPerformanceStats(),
    system: {
      locale: getCurrentLanguage(),
      messagesLoaded: Object.keys(safeI18n.messages).length,
      errorHandlerStats: I18nErrorHandler.getStats ? I18nErrorHandler.getStats() : null
    }
  }
}

// 批量翻译函数（移动端优化）
function batchTranslate(translationTasks) {
  return mobileOptimizer.batchTranslate(translationTasks, safeI18n.t)
}

export {
  initLanguage,
  setLanguage,
  getCurrentLanguage,
  getI18nPerformanceStats,  // 导出性能统计函数
  batchTranslate,           // 导出批量翻译函数
  i18n as rawI18nInstance,  // 导出原始实例（仅供内部使用）
  I18nErrorHandler,         // 导出错误处理器
  I18N_ERROR_TYPES,         // 导出错误类型常量
  resourceCache,            // 导出资源缓存管理器
  mobileOptimizer           // 导出移动端优化器
}